import clsx from 'clsx';
import type { FC } from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { type LinksFunction } from '@remix-run/node';

import {
  SharedAriaLink,
  links as sharedAriaLinkLinks,
} from '@/components/shared/shared-aria-link/shared-aria-link';
import {
  SharedFeatured,
  links as sharedFeaturedLinks,
} from '@/components/shared/shared-featured/shared-featured';
import { SharedIcon } from '@/components/shared/shared-icon/shared-icon';

import { AURA_PRICING_URL, AURA_YOUTUBE_VIDEO_URL } from '@/constants/urls';

import type { HackerViewBenefitsProps } from './hacker-view-benefits.types';

import videoPreview from './img/video-preview.jpg?url';
import videoPreviewX2 from './img/video-preview@2x.jpg?url';

import styles from './hacker-view-benefits.css?url';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  ...sharedFeaturedLinks(),
  ...sharedAriaLinkLinks(),
];

export const HackerViewBenefits: FC<HackerViewBenefitsProps> = ({
  title,
  subtitle,
  description,
  withCTA = true,
  withStats = true,
  withVideo,
  withFeatured = true,
  theme = 'dark',
  className = '',
}) => {
  const [isVideoVisible, setIsVideoVisible] = useState(false);

  const classes = clsx('hacker-view-benefits', className, {
    'hacker-view-benefits--dark': theme === 'dark',
    'hacker-view-benefits--light': theme === 'light',
  });

  const handleVideoClick = () => {
    setIsVideoVisible(true);
  };

  const handleOutVideoClick = () => {
    setIsVideoVisible(false);
  };

  return (
    <>
      <section className={classes}>
        <h2 className="hacker-view-benefits__title">{title}</h2>
        {subtitle && (
          <h3 className="hacker-view-benefits__subtitle">{subtitle}</h3>
        )}
        {description && (
          <p className="hacker-view-benefits__description">{description}</p>
        )}
        {withVideo && (
          <SharedAriaLink
            className="hacker-view-benefits__video-preview"
            onClick={handleVideoClick}
          >
            <img
              className="hacker-view-benefits__preview-img"
              src={videoPreview}
              srcSet={`${videoPreview} 1x, ${videoPreviewX2} 2x`}
              alt="Video Preview"
            />

            <div className="hacker-view-benefits__preview-play">
              <SharedIcon name="play" />
            </div>
          </SharedAriaLink>
        )}
        {withStats && (
          <ul className="hacker-view-benefits__list">
            <li className="hacker-view-benefits__item">
              <SharedIcon name="user-protected" />
              <h4 className="hacker-view-benefits__list-item-title">
                $1M ID Theft Insurance
              </h4>
              <p>
                Covers financial losses and legal fees due to identity theft.*
              </p>
            </li>
            <li className="hacker-view-benefits__item">
              <SharedIcon name="bell" />
              <h4 className="hacker-view-benefits__list-item-title">
                Super fast alerts
              </h4>
              <p>
                Get alerted to fraud in minutes, not days—up to 250x faster than
                the competition.**
              </p>
            </li>
            <li className="hacker-view-benefits__item">
              <SharedIcon name="chat" />
              <h4 className="hacker-view-benefits__list-item-title hacker-view-benefits__list-item-title--multiline">
                Help 24/7
              </h4>
              <p>
                U.S.-based customer Customer Support specialists are available
                to help around the clock.
              </p>
            </li>
          </ul>
        )}
        {withCTA && (
          <SharedAriaLink
            className="hacker-view-benefits__cta"
            href={`${AURA_PRICING_URL}?utm_campaign=digital-footprint-checker`}
            intent="primary"
          >
            Protect My Privacy
            <SharedIcon name="arrow-right" />
          </SharedAriaLink>
        )}
        {withFeatured && <SharedFeatured isCentered theme={theme} />}
      </section>
      {isVideoVisible &&
        createPortal(
          <SharedAriaLink
            className="hacker-view-benefits__video"
            onClick={handleOutVideoClick}
          >
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              frameBorder={0}
              allowFullScreen
              title="Aura video"
              src={AURA_YOUTUBE_VIDEO_URL}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </SharedAriaLink>,
          document.body,
        )}
    </>
  );
};
